import clsx from 'clsx'
import { TCGS } from '../../constants/tcgs'
import { TcgLogo } from '../tcg/TcgLogo'

type Props = {
  value: string[]
  onChange: (next: string[]) => void
  // 'labeled' shows the game name as a caption below each logo (used in the
  // Friends edit view). 'logos' drops the caption so players who already
  // recognize the marks get a cleaner row; if a logo asset is missing, the
  // fallback shows the full game name instead of initials so it's still
  // clear what each chip is.
  variant?: 'labeled' | 'logos'
}

export function TCGSelector({ value, onChange, variant = 'labeled' }: Props) {
  const showCaption = variant === 'labeled'
  const fallbackVariant = variant === 'logos' ? 'name' : 'initials'

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])
  }
  return (
    <div className="-mx-5">
      <div className="no-scrollbar flex gap-3 overflow-x-auto px-5 py-1">
        {TCGS.map((tcg) => {
          const selected = value.includes(tcg.id)
          return (
            <button
              key={tcg.id}
              type="button"
              onClick={() => toggle(tcg.id)}
              aria-pressed={selected}
              aria-label={tcg.name}
              className={clsx(
                'group flex w-[100px] shrink-0 flex-col items-center gap-1.5 rounded-2xl px-2 py-3 transition-all',
                selected
                  ? 'bg-primary-500/15 ring-1 ring-primary-400'
                  : 'bg-white/[0.03] ring-1 ring-white/5',
              )}
            >
              <div
                className={clsx(
                  // bg only shows for the initials/name fallback; the gray box
                  // disappears as soon as a real <img> renders so color logos
                  // get full horizontal presence
                  'flex h-[72px] w-full items-center justify-center rounded-xl bg-white/[0.04] transition-opacity has-[img]:bg-transparent',
                  selected ? 'opacity-100' : 'opacity-50',
                )}
              >
                <TcgLogo
                  tcg={tcg}
                  imgClassName="max-h-[64px] max-w-full object-contain"
                  fallbackClassName={
                    variant === 'logos' ? 'text-[11px]' : 'text-base'
                  }
                  fallbackVariant={fallbackVariant}
                />
              </div>
              {showCaption && (
                <div
                  className={clsx(
                    'line-clamp-2 min-h-[28px] text-center text-[11px] leading-tight',
                    selected ? 'text-white' : 'text-theme-text-muted',
                  )}
                >
                  {tcg.name}
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
